import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, Container, Row, Spinner, Table } from 'react-bootstrap';
import { FiCheckCircle, FiClock, FiFileText, FiPackage } from 'react-icons/fi';
import { fileAPI, projectAPI } from '../utils/api';
import './DashboardPage.css';

const SCMDashboardPage = ({ departmentId = null, departmentName = 'SCM' }) => {
  const [projects, setProjects] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchScmData = async () => {
      try {
        setLoading(true);
        setError('');

        const [projectsResponse, filesResponse] = await Promise.all([
          projectAPI.getAllProjects(),
          fileAPI.getAllFiles()
        ]);

        const allProjects = Array.isArray(projectsResponse?.data) ? projectsResponse.data : [];
        const rawFiles = filesResponse?.data;
        const allFiles = Array.isArray(rawFiles)
          ? rawFiles
          : Array.isArray(rawFiles?.files)
            ? rawFiles.files
            : [];

        const scopedProjects = departmentId
          ? allProjects.filter((project) => Number(project.departmentId) === Number(departmentId))
          : allProjects;

        const scopedProjectIds = new Set(scopedProjects.map((project) => Number(project.id)));
        const scopedFiles = allFiles.filter((file) => scopedProjectIds.has(Number(file.projectId)));

        setProjects(scopedProjects);
        setFiles(scopedFiles);
      } catch (err) {
        setError('Failed to load SCM dashboard data.');
      } finally {
        setLoading(false);
      }
    };

    fetchScmData();
  }, [departmentId]);

  const stats = useMemo(() => {
    const totalProjects = projects.length;
    const activeProjects = projects.filter((project) => String(project.status || '').toUpperCase() === 'ACTIVE').length;
    const completedProjects = projects.filter((project) => String(project.status || '').toUpperCase() === 'COMPLETED').length;
    const totalFiles = files.length;

    return {
      totalProjects,
      activeProjects,
      completedProjects,
      totalFiles
    };
  }, [projects, files]);

  const recentProjects = useMemo(() => {
    return [...projects]
      .sort((a, b) => {
        const aDate = new Date(a?.updatedAt || a?.createdAt || 0).getTime();
        const bDate = new Date(b?.updatedAt || b?.createdAt || 0).getTime();
        return bDate - aDate;
      })
      .slice(0, 8);
  }, [projects]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 mb-0">Loading SCM dashboard...</p>
      </div>
    );
  }

  return (
    <Container fluid className="dashboard-page py-4">
      {error && <Alert variant="danger">{error}</Alert>}

      <Row className="mb-4 g-3">
        <Col md={3}>
          <MetricCard title="Total Projects" value={stats.totalProjects} icon={<FiPackage size={22} />} variant="primary" />
        </Col>
        <Col md={3}>
          <MetricCard title="Active Projects" value={stats.activeProjects} icon={<FiClock size={22} />} variant="info" />
        </Col>
        <Col md={3}>
          <MetricCard title="Completed Projects" value={stats.completedProjects} icon={<FiCheckCircle size={22} />} variant="success" />
        </Col>
        <Col md={3}>
          <MetricCard title="Project Files" value={stats.totalFiles} icon={<FiFileText size={22} />} variant="secondary" />
        </Col>
      </Row>

      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Header className="bg-white border-bottom">
              <h5 className="mb-0">Recent SCM Projects</h5>
            </Card.Header>
            <Card.Body>
              {recentProjects.length === 0 ? (
                <Alert variant="info" className="mb-0">
                  No SCM projects found for this department.
                </Alert>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Project Name</th>
                        <th>Code</th>
                        <th>Status</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentProjects.map((project, index) => (
                        <tr key={project.id || index}>
                          <td>{index + 1}</td>
                          <td>{project.name || '-'}</td>
                          <td>{project.projectCode || '-'}</td>
                          <td>{project.status || '-'}</td>
                          <td>{formatDate(project.startDate)}</td>
                          <td>{formatDate(project.endDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

const MetricCard = ({ title, value, icon, variant = 'primary' }) => {
  return (
    <Card className={`stat-card stat-${variant} shadow-sm h-100`}>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <p className="stat-title">{title}</p>
            <h2 className="stat-value">{value}</h2>
          </div>
          <div className="stat-icon">{icon}</div>
        </div>
      </Card.Body>
    </Card>
  );
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

export default SCMDashboardPage;
